/**
 * useMapZoomPan.js
 * Zoom + pan for a canvas-rendered map, extracted from LiveRobotPositionViewer.jsx.
 * The transform lives in a ref and is applied directly to the canvas element's
 * style (never an ancestor), so panning/zooming never triggers a React re-render
 * or redraw. `toCanvasPoint` converts a mouse event into native canvas pixel
 * coordinates, which is what click-to-place-a-pin needs.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const MIN_SCALE = 0.25;
export const MAX_SCALE = 12;
// Per-notch zoom factor for a physical mouse wheel.
const WHEEL_STEP = 1.12;

const clamp = (v) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, v));

const useMapZoomPan = ({
  mapSize,
  enabled = true,
  panEnabled = true,
  // Editors leave this false: blowing an occupancy grid past 1:1 invents
  // resolution the map does not have, and a pin placed on a blurry pixel is a
  // real coordinate error. Read-only VIEWERS opt in — there, a small map
  // rendered at native size in a large panel is just dead space, and being able
  // to see the points at a glance is the entire point of showing the map.
  allowUpscale = false,
  // Ceiling for that upscale. Past ~2.5x an occupancy grid stops reading as a
  // floor plan and starts reading as artifacts.
  maxFitScale = 2.5,
} = {}) => {
  const wrapEl = useRef(null);
  const canvasRef = useRef(null);
  // Bumped whenever the wrapper element mounts or unmounts. Callers commonly
  // render the map wrapper CONDITIONALLY (e.g. only once `!loading && pgmData`),
  // so a plain ref is still null when effects first run and the wheel/touch
  // listeners would never attach — leaving the browser to zoom the whole page.
  // Depending on this counter makes those effects re-run at the exact moment the
  // node appears.
  const [wrapVersion, setWrapVersion] = useState(0);
  const zoomRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const panRef = useRef({ active: false, startX: 0, startY: 0, startTx: 0, startTy: 0 });
  const touchRef = useRef({ mode: null, lastX: 0, lastY: 0, lastDist: 0 });
  // Read inside document-level listeners that are attached once.
  const panEnabledRef = useRef(panEnabled);
  useEffect(() => {
    panEnabledRef.current = panEnabled;
  }, [panEnabled]);

  // Callback ref handed to the wrapper element. Internal code reads the element
  // through `wrapEl` (a normal ref) rather than through this function, which
  // keeps the hook's dependency arrays honest.
  const wrapRef = useCallback((node) => {
    if (wrapEl.current === node) return;
    wrapEl.current = node;
    setWrapVersion((v) => v + 1);
  }, []);

  // Callers typically pass a fresh {width,height} literal each render. Depending
  // on that object directly would give every returned callback a new identity on
  // every render — and a consumer that publishes those callbacks via setState
  // would then re-render forever. Depend on the NUMBERS instead.
  const mapW = mapSize?.width || 0;
  const mapH = mapSize?.height || 0;

  const applyTransform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { scale, tx, ty } = zoomRef.current;
    canvas.style.transformOrigin = "0 0";
    canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  }, []);

  // Scale that fits the whole map in the wrapper, centred. Capped at 1 unless
  // the caller opted into upscaling (see `allowUpscale`).
  const fitToView = useCallback(() => {
    const wrap = wrapEl.current;
    if (!wrap || !mapW || !mapH) return;
    const cw = wrap.clientWidth || 800;
    const ch = wrap.clientHeight || 600;
    const ceiling = allowUpscale ? maxFitScale : 1;
    const fit = Math.min(cw / mapW, ch / mapH, ceiling);
    zoomRef.current = { scale: fit, tx: (cw - mapW * fit) / 2, ty: (ch - mapH * fit) / 2 };
    applyTransform();
  }, [mapW, mapH, applyTransform, allowUpscale, maxFitScale]);

  // Zoom by `factor` about the wrapper-relative point (mx, my).
  const zoomAtPoint = useCallback(
    (mx, my, factor) => {
      const { scale: s, tx, ty } = zoomRef.current;
      const ns = clamp(s * factor);
      if (ns === s) return;
      zoomRef.current = {
        scale: ns,
        tx: mx - (mx - tx) * (ns / s),
        ty: my - (my - ty) * (ns / s),
      };
      applyTransform();
    },
    [applyTransform]
  );

  // Button zoom keeps the CENTRE of the viewport fixed, which is what a user
  // expects from a +/- control (cursor position is irrelevant there).
  const zoomByStep = useCallback(
    (factor) => {
      const wrap = wrapEl.current;
      if (!wrap) return;
      zoomAtPoint(wrap.clientWidth / 2, wrap.clientHeight / 2, factor);
    },
    [zoomAtPoint]
  );

  const zoomIn = useCallback(() => zoomByStep(1.5), [zoomByStep]);
  const zoomOut = useCallback(() => zoomByStep(1 / 1.5), [zoomByStep]);

  // Fit once the map's dimensions are known (and again if they change, i.e. a
  // different map loaded).
  useEffect(() => {
    if (!enabled || !mapW || !mapH) return;
    const raf = requestAnimationFrame(() => fitToView());
    return () => cancelAnimationFrame(raf);
    // wrapVersion: the wrapper must exist to be measured — if the map data
    // arrives before it mounts, this has to run again once it does.
  }, [enabled, mapW, mapH, fitToView, wrapVersion]);

  // Re-fit when the wrapper itself resizes — entering/leaving full screen, a
  // window resize, or the sidebar collapsing all change the available box.
  useEffect(() => {
    const wrap = wrapEl.current;
    if (!enabled || !wrap || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => fitToView());
    ro.observe(wrap);
    return () => ro.disconnect();
    // wrapVersion: observe the new node when the wrapper remounts.
  }, [enabled, fitToView, wrapVersion]);

  // Wheel zoom. Must be non-passive: the listener calls preventDefault to stop
  // the page scrolling behind the map, which React's synthetic onWheel cannot do.
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();
      const wrap = wrapEl.current;
      if (!wrap) return;
      const { left, top } = wrap.getBoundingClientRect();
      const dir = e.deltaY < 0 ? 1 : -1;
      // Trackpad pinch arrives as a wheel event with ctrlKey set, with much finer
      // deltas than a mouse wheel — scale exponentially rather than in flat steps.
      const stepFactor = dir > 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      const pinchFactor = Math.exp(dir * Math.min(Math.abs(e.deltaY), 40) * 0.02);
      zoomAtPoint(e.clientX - left, e.clientY - top, e.ctrlKey ? pinchFactor : stepFactor);
    },
    [zoomAtPoint]
  );

  useEffect(() => {
    const el = wrapEl.current;
    if (!enabled || !el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
    // wrapVersion: re-attach when the (conditionally rendered) wrapper mounts.
  }, [enabled, handleWheel, wrapVersion]);

  // Touch: two fingers pinch-zoom, one finger pans.
  const touchDist = (t0, t1) => Math.hypot(t1.clientX - t0.clientX, t1.clientY - t0.clientY);
  const touchMid = (t0, t1) => ({
    x: (t0.clientX + t1.clientX) / 2,
    y: (t0.clientY + t1.clientY) / 2,
  });

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      const [t0, t1] = e.touches;
      touchRef.current = { mode: "pinch", lastDist: touchDist(t0, t1) };
    } else if (e.touches.length === 1 && panEnabledRef.current) {
      touchRef.current = { mode: "pan", lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    }
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      const wrap = wrapEl.current;
      if (!wrap) return;
      const t = touchRef.current;

      if (e.touches.length === 2 && t.mode === "pinch") {
        e.preventDefault();
        const [t0, t1] = e.touches;
        const dist = touchDist(t0, t1);
        const mid = touchMid(t0, t1);
        const { left, top } = wrap.getBoundingClientRect();
        if (t.lastDist > 0) zoomAtPoint(mid.x - left, mid.y - top, dist / t.lastDist);
        touchRef.current = { ...t, lastDist: dist };
        return;
      }

      if (e.touches.length === 1 && t.mode === "pan") {
        e.preventDefault();
        const touch = e.touches[0];
        zoomRef.current.tx += touch.clientX - t.lastX;
        zoomRef.current.ty += touch.clientY - t.lastY;
        applyTransform();
        touchRef.current = { ...t, lastX: touch.clientX, lastY: touch.clientY };
      }
    },
    [zoomAtPoint, applyTransform]
  );

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      touchRef.current = { mode: null, lastX: 0, lastY: 0, lastDist: 0 };
    } else if (e.touches.length === 1) {
      touchRef.current = { mode: "pan", lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    }
  }, []);

  useEffect(() => {
    const el = wrapEl.current;
    if (!enabled || !el) return;
    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });
    el.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      el.removeEventListener("touchcancel", handleTouchEnd);
    };
    // wrapVersion: same conditional-mount reason as the wheel effect above.
  }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd, wrapVersion]);

  // Mouse pan. `panEnabled` lets the host suppress it while another drag
  // gesture owns the pointer (e.g. rotating a pending map pin).
  const onMouseDown = useCallback((e) => {
    if (e.button !== 0 || !panEnabledRef.current) return;
    panRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      startTx: zoomRef.current.tx,
      startTy: zoomRef.current.ty,
    };
    if (wrapEl.current) wrapEl.current.style.cursor = "grabbing";
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      if (!panRef.current.active) return;
      zoomRef.current.tx = panRef.current.startTx + (e.clientX - panRef.current.startX);
      zoomRef.current.ty = panRef.current.startTy + (e.clientY - panRef.current.startY);
      applyTransform();
    },
    [applyTransform]
  );

  const onMouseUp = useCallback(() => {
    if (!panRef.current.active) return;
    panRef.current.active = false;
    if (wrapEl.current) wrapEl.current.style.cursor = "grab";
  }, []);

  /** Shift the view by a pixel delta — the keyboard equivalent of drag-to-pan. */
  const panBy = useCallback(
    (dx, dy) => {
      zoomRef.current.tx += dx;
      zoomRef.current.ty += dy;
      applyTransform();
    },
    [applyTransform]
  );

  /**
   * Convert a mouse event to NATIVE canvas pixel coordinates, undoing both the
   * scale and the translation. Click handling must go through this — the
   * canvas's bounding rect reflects the transformed box, so raw
   * (clientX - rect.left) lands on the wrong pixel at any scale or pan offset.
   */
  const toCanvasPoint = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const { scale } = zoomRef.current;
    return {
      x: (e.clientX - rect.left) / scale,
      y: (e.clientY - rect.top) / scale,
    };
  }, []);

  /** True while a pan drag is in progress — lets the host ignore the click that follows. */
  const isPanning = useCallback(() => panRef.current.active, []);

  return {
    wrapRef,
    canvasRef,
    zoomRef,
    applyTransform,
    fitToView,
    zoomIn,
    zoomOut,
    toCanvasPoint,
    isPanning,
    panBy,
    panHandlers: { onMouseDown, onMouseMove, onMouseUp, onMouseLeave: onMouseUp },
  };
};

export default useMapZoomPan;
